import {
  computed,
  inject,
  Injectable,
  Signal,
} from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { HttpTransportType, HubConnectionState } from '@microsoft/signalr';
import { Observable, of, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { INotification } from '../entities/client-notifications.interface';
import { EClientNotificationsEventTypes } from '../entities/client-notifications.enum';
import { APP_CONFIG } from '@application/config';
import { AuthStore, IAuthAccessToken } from '@auth/data-access';
import { DateTimeService } from '@application/data-access';
/**
 * Reconnection that never gives up, aligned with the edge hub
 * (`AccessPointEdgeHubService`): first attempt right away, then exponential
 * backoff capped at `maxDelayMs` (60s, from the service).
 *
 * What used to be here was `[5000, 5000, 5000, 10000]`, and that array bought
 * exactly **25 seconds**: SignalR stores `[...delays, null]`
 * (`DefaultReconnectPolicy`) and gives up for good once it reaches the
 * `null`, firing `onclose`. Any outage longer than that — a network switch, a
 * VPN coming back, a suspended machine, a backend deploy — killed the feed
 * silently until someone reloaded the page, because no `openConnection`
 * consumer listens to `onConnectionClose`.
 */
class AggressiveReconnectPolicy implements signalR.IRetryPolicy {
  private readonly baseDelayMs = 500;

  constructor(private readonly maxDelayMs: number) {}

  public nextRetryDelayInMilliseconds(
    retryContext: signalR.RetryContext
  ): number {
    if (retryContext.previousRetryCount === 0) {
      return 0;
    }

    const attempt = retryContext.previousRetryCount - 1;
    const delay = this.baseDelayMs * Math.pow(2, attempt);

    return Math.min(delay, this.maxDelayMs);
  }
}

@Injectable({
  providedIn: 'root'
})
export class ClientNotificationService {
  private readonly authStore = inject(AuthStore);
  private readonly authData: Signal<IAuthAccessToken | null> = this.authStore.authData;
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;
  private readonly dateTimeService = inject(DateTimeService);

  /**
   * How long to wait before bringing back a connection the server closed for
   * good. Same value `AccessPointEdgeHubService` uses in its own `onclose`, and
   * the ceiling of the reconnection backoff.
   */
  private static readonly RECONNECT_AFTER_CLOSE_MS = 60000;

  /** Shared instance: the policy is stateless, the delay comes from the context. */
  private static readonly RECONNECT_POLICY = new AggressiveReconnectPolicy(
    ClientNotificationService.RECONNECT_AFTER_CLOSE_MS
  );

  /**
   * Campos que carregam um instante UTC apesar de o nome não terminar em `Utc`,
   * por evento. É o equivalente, do lado do hub, ao
   * `withUtcDateFields([...])` que o pedido HTTP declara — a convenção do
   * sufixo não os apanha, e sem isto o mesmo campo chega convertido por HTTP e
   * cru por WebSocket.
   *
   * A procura é por nome de evento; os eventos de ciclo de vida da ligação
   * (`onConnected`, `onConnectionClose`, ...) não constam e ficam sem extras,
   * exatamente como antes. Vazio não desliga nada: `toLocalObject` continua a
   * converter todo campo terminado em `Utc` de qualquer evento — o mapa só
   * acrescenta os que fogem à convenção.
   *
   * Esteve aqui `onAccessNotification: ['EntryedOn']`, e era engano. O
   * `EntryedOn` respeita o contrato do sufixo: sem `Utc` no nome, é hora de
   * parede da conta, e o `Z` que traz é decorativo. A entrada foi acrescentada
   * para alinhar a linha do hub com a linha recarregada por HTTP, mas essa
   * estava a ser convertida a mais — alinhou as duas no valor errado, e a
   * grade de "Entradas Recentes" passou a mostrar "há 3 horas" para uma
   * entrada acabada de acontecer. Antes de declarar um campo aqui, confirme na
   * resposta crua que os dígitos são mesmo UTC.
   */
  private static readonly EVENT_UTC_DATE_FIELDS: Readonly<
    Record<string, readonly string[]>
  > = {};

  /**
   * Conexões abertas, uma por identidade. A URL do hub já leva o tenant e a
   * pessoa (e o serviço, `client_notification` ou `data_service`), por isso é
   * ela a chave.
   *
   * Antes, cada `openConnection` abria o seu próprio WebSocket para a mesma
   * URL: a barra de notificações, o acesso, o consumo, a assinatura e o avatar
   * do perfil somavam vários sockets por aba. Todos entravam nos mesmos grupos
   * do hub, portanto cada evento do tenant chegava a todos eles e só o que
   * tinha o handler o aproveitava; numa queda de réplica, cada um reconectava
   * por conta própria, ao mesmo tempo. Agora cada assinante pendura os seus
   * handlers na conexão da sua identidade, e ela fecha quando o último sai.
   */
  private readonly sharedHubs = new Map<
    string,
    {
      connection: signalR.HubConnection;
      lifecycle: Subject<{ eventName: string; eventData: unknown }>;
      subscribers: number;
      startPromise: Promise<void>;
    }
  >();

  public openConnection(
    eventNames: string[],
    url?: string,
    tenantId?: string,
    personId?: string
  ): Observable<any> {
    const actualTenantId = tenantId ?? this.authData()?.TenantId;
    const actualPersonId = personId ?? this.authData()?.PersonId;

    return new Observable((obs) => {
      // Without an authenticated user the hub URL would carry `UserId=undefined`;
      // skip opening the socket instead of connecting with a broken identity.
      if (!actualPersonId) {
        obs.complete();
        return () => undefined;
      }

      const hubUrl =
        this.apiUrl?.[url ?? 'client_notification'] +
        `/NotificationHub?TenantId=${actualTenantId}&UserId=${actualPersonId}`;
      const hub = this.acquireHub(hubUrl);

      const deliver = (res: { eventName: string; eventData: unknown }): void =>
        obs.next(
          this.dateTimeService.toLocalObject(
            res,
            ClientNotificationService.EVENT_UTC_DATE_FIELDS[res.eventName]
          )
        );

      // A referência do handler fica guardada: o `off` sem ela tiraria os
      // handlers de TODOS os assinantes daquele evento na conexão partilhada.
      const handlers = eventNames.map((eventName) => {
        const handler = (data: any): void =>
          deliver({ eventName, eventData: this.toEventData(eventName, data) });
        hub.connection.on(eventName, handler);
        return { eventName, handler };
      });

      const lifecycleSubscription = hub.lifecycle.subscribe(deliver);

      // Quem chega com a conexão já aberta perdeu o `onConnected` dela; recebe
      // o seu, como recebia quando cada assinante tinha a própria conexão.
      if (hub.connection.state === HubConnectionState.Connected) {
        Promise.resolve().then(() => {
          if (!lifecycleSubscription.closed) {
            deliver({ eventName: 'onConnected', eventData: 'ok' });
          }
        });
      }

      return () => {
        handlers.forEach(({ eventName, handler }) =>
          hub.connection.off(eventName, handler)
        );
        lifecycleSubscription.unsubscribe();
        this.releaseHub(hubUrl);
      };
    });
  }

  /**
   * Devolve a conexão da identidade, abrindo-a se ainda não existir.
   *
   * `onclose`, `onreconnecting` e `onreconnected` são registados uma única vez
   * por conexão e repassados pelo `lifecycle`: o SignalR não tem `off` para
   * eles, e registá-los por assinante acumularia um callback a cada tela que
   * abre e fecha, numa conexão que agora vive a sessão toda.
   */
  private acquireHub(hubUrl: string): {
    connection: signalR.HubConnection;
    lifecycle: Subject<{ eventName: string; eventData: unknown }>;
  } {
    const existing = this.sharedHubs.get(hubUrl);
    if (existing) {
      this.sharedHubs.set(hubUrl, {
        ...existing,
        subscribers: existing.subscribers + 1,
      });
      return existing;
    }

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect(ClientNotificationService.RECONNECT_POLICY)
      .build();
    const lifecycle = new Subject<{ eventName: string; eventData: unknown }>();
    const emit = (eventName: string, eventData: unknown): void =>
      lifecycle.next({ eventName, eventData });
    // Aposentada = saiu do mapa (último assinante saiu, ou outra conexão já
    // ocupa a mesma identidade). Nenhuma tentativa de reabrir passa disto.
    const isRetired = (): boolean =>
      this.sharedHubs.get(hubUrl)?.connection !== connection;

    connection.onclose((e) => {
      emit('onConnectionClose', e);
      this.scheduleRestart(connection, emit, isRetired);
    });
    connection.onreconnecting((e) => emit('onConnectionReconnecting', e));
    connection.onreconnected((e) => emit('onConnectionReconnected', e));

    const hub = {
      connection,
      lifecycle,
      subscribers: 1,
      startPromise: Promise.resolve(),
    };
    // Entra no mapa antes do `start`: o `isRetired` é consultado logo à
    // entrada do `startWithRetry`.
    this.sharedHubs.set(hubUrl, hub);
    this.sharedHubs.set(hubUrl, {
      ...hub,
      startPromise: this.startWithRetry(connection, emit, isRetired),
    });

    return hub;
  }

  /**
   * Largada de um assinante. O último a sair fecha a conexão.
   *
   * A entrada sai do mapa já, e não quando o `stop()` terminar: quem chegar a
   * seguir abre uma conexão nova em vez de se pendurar numa que está a fechar.
   */
  private releaseHub(hubUrl: string): void {
    const hub = this.sharedHubs.get(hubUrl);
    if (!hub) return;

    if (hub.subscribers > 1) {
      this.sharedHubs.set(hubUrl, { ...hub, subscribers: hub.subscribers - 1 });
      return;
    }

    this.sharedHubs.delete(hubUrl);
    hub.lifecycle.complete();
    // Wait for the handshake to settle before stopping. Calling `stop()`
    // while the connection is still `Connecting` cancels the handshake and
    // makes SignalR log "Handshake was canceled".
    hub.startPromise
      .then(() => hub.connection.stop())
      .catch(() => undefined);
  }

  /**
   * Corpo do evento na forma que os consumidores esperam: PascalCase para os
   * eventos de notificação, e os envelopes (`Person`, `Consumption`, ...) dos
   * que os usam.
   */
  private toEventData(eventName: string, data: any): any {
    const eventData: any = {};

    const defaultProccess: string[] = [
      EClientNotificationsEventTypes.onCentralNotification,
      EClientNotificationsEventTypes.onCentralNotificationContract,
      'onUpdatePersonDatasNotification',
      'onAccessNotification',
      EClientNotificationsEventTypes.onGympassCheckinNotification,
      EClientNotificationsEventTypes.onTotalpassCheckinNotification,
      EClientNotificationsEventTypes.onUserPhotoUploadedNotification,
      EClientNotificationsEventTypes.onInvoicesZipCreatedNotification,
      EClientNotificationsEventTypes.onCrmEventNotification,
    ];
    const personProccess = [
      'onGympassCheckinNotification',
      'onTotalpassCheckinNotification'
    ];

    const consumptionProccess = [
      'onCompletedConsumptionNotification'
    ];

    if (defaultProccess.includes(eventName)) {
      for (const [key, value] of Object.entries(data)) {
        eventData[this.turnPascalCase(key)] = value;
      }

    }

    if (personProccess.includes(eventName)) {
      eventData['Person'] = {};
      for (const [key, value] of Object.entries(data.person)) {
        eventData['Person'][this.turnPascalCase(key)] = value;
      }
    }

    if (consumptionProccess.includes(eventName)) {
      eventData['Consumption'] = {};
      for (const [key, value] of Object.entries(data.consumption || data || {})) {
        eventData['Consumption'][this.turnPascalCase(key)] = value;
      }
    }

    if (eventName === EClientNotificationsEventTypes.onAccessRealTimeOccupancyNotification) {
      eventData['CurrentOccupancy'] = data;
    }

    if (eventName === EClientNotificationsEventTypes.onSubscriptionReceivementNotification) {
      eventData['Receivement'] = { ...data };
    }

    if (eventName === EClientNotificationsEventTypes.onReceivementErrorNotification) {
      eventData['ReceivementError'] = { ...data };
    }

    return eventData;
  }

  /**
   * Opens the connection and keeps trying until it opens, or until the
   * connection is retired (its last subscriber left).
   *
   * `withAutomaticReconnect` does not cover either of the two paths that leave
   * the connection down: the initial `start()` that fails, and the `start()`
   * of a restart after `onclose`. SignalR's automatic retry only kicks in for
   * a connection that did manage to open — before this, a failed first
   * connection (page opened offline, backend still booting, token expiring)
   * called `observer.error`, the outer subscriber completed, and the feed
   * stayed dead until someone reloaded the page.
   *
   * The returned promise settles once the first attempt has been handled, so
   * the teardown can await it before calling `stop()`; later attempts run on
   * their own.
   */
  private async startWithRetry(
    connection: signalR.HubConnection,
    emit: (eventName: string, eventData: unknown) => void,
    isRetired: () => boolean,
    attempt = 0
  ): Promise<void> {
    if (isRetired()) return;

    try {
      await connection.start();
      emit('onConnected', 'ok');
    } catch (error) {
      // Bailing out mid-handshake is expected teardown noise, not a real
      // failure.
      if (isRetired()) return;

      // Do not error the stream: erroring ends it, and an ended stream never
      // connects again. Listeners see the same event they would see on a
      // drop, and get `onConnected` once it catches.
      emit('onConnectionReconnecting', error);

      const retryDelayMs = ClientNotificationService.RECONNECT_POLICY.nextRetryDelayInMilliseconds({
        previousRetryCount: attempt,
        elapsedMilliseconds: 0,
        retryReason: error instanceof Error ? error : new Error(String(error)),
      });

      this.retryLater(connection, emit, isRetired, attempt + 1, retryDelayMs);
    }
  }

  /**
   * Brings back a connection the server closed for good. Repeats indefinitely,
   * like the edge hub, because a failed `start()` does not fire `onclose`
   * again — without this, a single failed restart left the feed dead until
   * someone reloaded the page.
   */
  private scheduleRestart(
    connection: signalR.HubConnection,
    emit: (eventName: string, eventData: unknown) => void,
    isRetired: () => boolean
  ): void {
    this.retryLater(
      connection,
      emit,
      isRetired,
      0,
      ClientNotificationService.RECONNECT_AFTER_CLOSE_MS
    );
  }

  private retryLater(
    connection: signalR.HubConnection,
    emit: (eventName: string, eventData: unknown) => void,
    isRetired: () => boolean,
    attempt: number,
    delayMs: number
  ): void {
    setTimeout(() => {
      if (isRetired()) return;
      if (connection.state !== HubConnectionState.Disconnected) return;
      this.startWithRetry(connection, emit, isRetired, attempt);
    }, delayMs);
  }

  private turnPascalCase(key: string): string {
    return `${key[0].toUpperCase()}${key.substring(1, key.length)}`;
  }

  /**
   * O par tenant/pessoa do token, ou `null` se faltar algum. O logout põe
   * `authData` a null — ver {@link getNotifications} para o que acontecia sem
   * esta pergunta.
   */
  private sessionIds(): { tenantId: string; personId: string } | null {
    const auth = this.authData();
    if (!auth?.TenantId || !auth?.PersonId) {
      return null;
    }

    return { tenantId: auth.TenantId, personId: auth.PersonId };
  }

  /**
   * `tenantId:personId` da sessão, ou `null` se faltar algum dos dois.
   *
   * É signal e não um método por causa de quem o consome: o store precisa de
   * reagir à sessão a *aparecer* (login sem reload da página) e a *mudar*
   * (troca de conta pelo `changePersonAccount`, que troca o `authData` no
   * lugar, sem recarregar), não só de a consultar uma vez no arranque.
   *
   * O par inteiro, e não um booleano: as notificações são por pessoa, portanto
   * trocar de conta tem de recarregar a lista — com um booleano a mudança
   * passava despercebida e ficava a lista da pessoa anterior no ecrã.
   */
  public readonly sessionKey = computed(() => {
    const session = this.sessionIds();
    return session ? `${session.tenantId}:${session.personId}` : null;
  });

  /**
   * A elevação temporária (`beginTemporaryAuthSession`) troca o `authData` por
   * o de OUTRO utilizador — o supervisor que autorizou — durante ~30s, e depois
   * repõe o anterior. Não é troca de conta, e quem seguir a {@link sessionKey}
   * tem de a ignorar, ou recarrega a lista com a identidade do supervisor e
   * volta a recarregar no fim. Exposto aqui para o store não ter de importar o
   * `AuthStore` e acrescentar mais uma aresta ao ciclo que o
   * `enforce-module-boundaries` já assinala nesta lib.
   */
  public readonly isTemporaryAuthActive = this.authStore.isTemporaryAuthActive;

  /**
   * Sem sessão não há pedido nenhum a fazer. A guarda existe porque a URL é um
   * template string: com `authData` a null, `${this.authData()?.TenantId}` não
   * rebenta — escreve o **texto** `undefined` na query, e o backend responde
   * `400 The value 'undefined' is not valid.`. É o mesmo caso já tratado em
   * {@link openConnection}, e chega aqui pelo caminho do logout: o refresh
   * falha com 400, o `TokenInterceptor` chama `AuthStore.logout()` (que põe
   * `authData` a null) e a SPA continua viva durante o redirect, tempo mais que
   * suficiente para este store carregar a lista.
   *
   * Devolve lista vazia em vez de `EMPTY` de propósito: quem chama marca
   * `loadNotifications` como *loading* antes do `switchMap`, e um observable que
   * completa sem emitir deixava esse estado preso — esqueleto para sempre no
   * sino e no deck.
   */
  public getNotifications(
    page: number,
    take: number,
    expandContent: boolean,
    origin?: string
  ): Observable<INotification[]> {
    const session = this.sessionIds();
    if (!session) {
      return of([]);
    }

    const originParam = origin ? `&origin=${origin}` : '';

    return this.http.get<INotification[]>(
      this.apiUrl.client_notification +
      `/Notifications/GetNotifications?tenantId=${session.tenantId}&userId=${session.personId}&page=${page}&take=${take}&expandContent=${expandContent}${originParam}`,
      {
        withCredentials: true,
      }
    );
  }

  /** Mesma guarda e mesmo motivo do {@link getNotifications}. */
  public setReadedAllNotifications(): Observable<null> {
    const session = this.sessionIds();
    if (!session) {
      return of(null);
    }

    return this.http.post<null>(
      this.apiUrl.client_notification +
      `/Notifications/SetReaded?tenantId=${session.tenantId}&userId=${session.personId}`,
      {},
      {
        withCredentials: true,
      }
    );
  }

  public deleteNotification(notificationId: string): Observable<null> {
    return this.http.delete<null>(
      this.apiUrl.client_notification +
      `/Notifications/Delete`,
      {
        params: {
          notificationId
        },
        withCredentials: true,
      }
    );
  }
}
